import { $svg } from './svg';
import { UIClassesSpec } from './types';

export namespace $path {
    const pi = Math.PI;
    const tau = 2 * pi;
    const epsilon = 1e-6;
    const tauEpsilon = tau - epsilon;

    let xs: number | undefined;
    let ys: number | undefined;
    let x: number | undefined;
    let y: number | undefined;
    let path: (string | number)[];

    export function start() {
        xs = ys = x = y = undefined;
        path = [];
    }

    export function finish() {
        return path.join('');
    }

    export function moveTo(x1: number, y1: number) {
        path.push('M', xs = x = x1, ',', ys = y = y1);
    }

    export function close() {
        x = xs;
        y = ys;
        path.push('Z');
    }

    export function lineTo(x1: number, y1: number) {
        path.push('L', x = x1, ',', y = y1);
    }

    export function quadTo(x1: number, y1: number, x2: number, y2: number) {
        path.push('Q', x1, ',', y1, ',', x = x2, ',', y = y2);
    }

    export function bezierTo(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number) {
        path.push('C', x1, ',', y1, ',', x2, ',', y2, ',', x = x3, ',', y = y3);
    }

    export function arcTo(x1: number, y1: number, x2: number, y2: number, r: number) {
        if(x == undefined) {
            moveTo(x1, y1);
            return;
        }

        // const x0 = this._x1,
        //     y0 = this._y1,
        const x21 = x2 - x1,
            y21 = y2 - y1,
            x01 = x! - x1,
            y01 = y! - y1,
            l01_2 = x01 * x01 + y01 * y01;

        if(r < 0) throw new Error('negative radius!!');
        if(l01_2 <= epsilon) return;

        if(Math.abs(y01 * x21 - y21 * x01) <= epsilon || r === 0) {
            lineTo(x1, y1);
            return;
        }

        const x20 = x2 - x!,
            y20 = y2 - y!,
            l21_2 = x21 * x21 + y21 * y21,
            l20_2 = x20 * x20 + y20 * y20,
            l21 = Math.sqrt(l21_2),
            l01 = Math.sqrt(l01_2),
            l = r * Math.tan((pi - Math.acos((l21_2 + l01_2 - l20_2) / (2 * l21 * l01))) / 2),
            t01 = l / l01,
            t21 = l / l21;

        if(Math.abs(t01 - 1) > epsilon)
            lineTo(x1 + t01 * x01, y1 + t01 * y01);

        path.push('A', r, ',', r, ',0,0,', +(y01 * x20 > x01 * y20), ',', x = x1 + t21 * x21, ',', y = y1 + t21 * y21);
    }

    export function arc(x1: number, y1: number, r: number, a0: number, a1: number, ccw: boolean) {
        let dx = r * Math.cos(a0),
            dy = r * Math.sin(a0),
            x0 = x1 + dx,
            y0 = y1 + dy,
            cw = ccw ? 0 : 1,
            da = ccw ? a0 - a1 : a1 - a0;

        if(r < 0) throw new Error('negative radius!!');

        if(x == undefined)
            moveTo(x0, y0);
        else if(Math.abs(x - x0) > epsilon || Math.abs(y! - y0) > epsilon)
            lineTo(x0, y0);

        if(!r) return;

        if(da > tauEpsilon) {
            path.push('A', r, ',', r, ',0,1,', cw, ',', x1 - dx, ',', y1 - dy,
                'A', r, ',', r, ',0,1,', cw, ',', x = x0, ',', y = y0);
        } else {
            if(da < 0) da = da % tau + tau;
            path.push('A', r, ',', r, ',0,', +(da >= pi), ',', cw, ',', x = x1 + r * Math.cos(a1), ',', y = y1 + r * Math.sin(a1));
        }
    }

    export function rect(x1: number, y1: number, w: number, h: number) {
        path.push('M', xs = x = x1, ',', ys = y = y1, 'h', w, 'v', h, 'h', -w, 'Z');
    }

    export function bar(x1: number, y1: number, x2: number, y2: number, cr: number, ca: number) {
        const w_2 = (x2 - x1) / 2;
        const h = y1 - y2;
        if(h <= 0) return;

        const sin = Math.sin(ca), cos = Math.cos(ca);
        const sh = cr * sin;
        const bx = cr * (1 - cos);
        const br = (w_2 - bx) / cos;
        const xh = br - (w_2 - bx) * sin / cos;

        if(xh > h) {
            const a = Math.acos((br - h) / br);
            arc(x2 - w_2, y2 + br, br, -Math.PI / 2 + a, -Math.PI / 2 - a, true);
            close();
        } else if(sh + xh > h) {
            const a = Math.asin((sh + xh - h) / cr);
            arc(x2 - cr, y2 + sh + xh, cr, -a, -ca, true);
            arc(x2 - w_2, y2 + br, br, -ca, -Math.PI + ca, true);
            arc(x1 + cr, y2 + sh + xh, cr, -Math.PI + ca, -Math.PI + a, true);
            close();
        } else {
            moveTo(x1, y1);
            lineTo(x2, y1);
            lineTo(x2, y2 + sh + xh);
            arc(x2 - cr, y2 + sh + xh, cr, 0, -ca, true);
            arc(x2 - w_2, y2 + br, br, -ca, -Math.PI + ca, true);
            arc(x1 + cr, y2 + sh + xh, cr, -Math.PI + ca, -Math.PI, true);
            close();
        }
    }

    export function toElem(classes?: UIClassesSpec) {
        const elem = $svg.newElem('path', classes);
        elem.setAttribute('d', finish());
        return elem;
    }

    export function setTo(elem: SVGPathElement) {
        elem.setAttribute('d', finish());
    }
}
